import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ErrorState, Skeleton } from '@fiado/react-kit';
import { resolveErrorMessage } from '../../api/errorMapping';
import { platformApi } from '../../api/endpoints/platform';
import { useResolvedTenantLevels } from '../../hooks/useResolvedTenantLevels';
import { Gate } from '../../components/permissions/Gate';
import type { Permission, Scope } from '../../api/types/permissions';
import type { TenantLevelLocal } from '../../api/types/PlatformLocal';

// Shell del drill-down de un tenant: encabezado con su nombre y sub-nav de recursos sobre un <Outlet/>.
// Los tabs de recurso salen de `tenant.levels` (los niveles de su aplicación).
export const Route = createFileRoute('/_authenticated/_layout/platform/tenants/$tenantId')({
    component: TenantShell,
});

// Sub-nav del shell, extraído para testearlo sin el router file-based.
export function TenantSubNav({
    tenantId,
    displayName,
    isError = false,
    pathname,
    levels,
}: {
    tenantId: string;
    displayName?: string;
    /** Sin nombre y sin error, el nombre sigue cargando: se muestra un skeleton. */
    isError?: boolean;
    pathname: string;
    /** Niveles de RECURSO de la app del tenant (`tenant.levels`). Decide los tabs de recurso. */
    levels?: TenantLevelLocal[];
}) {
    const base = `/platform/tenants/${tenantId}`;
    const tabs = tabsForLevels(levels);
    return (
        <div className="space-y-4">
            <div>
                <p className="text-sm text-muted-foreground">Organización</p>
                {displayName ? (
                    <h1 className="text-2xl font-semibold">{displayName}</h1>
                ) : isError ? null : (
                    <div data-testid="tenant-name-skeleton">
                        <Skeleton variant="text" width={220} height={32} />
                    </div>
                )}
            </div>

            <nav aria-label="Recursos de la organización" className="flex flex-wrap gap-1 border-b">
                {tabs.map((tab) => {
                    const to = `${base}/${tab.to}`;
                    const isActive = pathname === to || pathname.startsWith(`${to}/`);
                    // La mayoría de las tabs (comunes + niveles) gatean a scope TENANT/tenantId (lo
                    // cubre el platform '*' por rank): los niveles con `tenant.level.manage`. La tab
                    // Campos usa un permiso `platform.*` a scope PLATFORM (global, sin scopeRef de tenant).
                    const isPlatformScoped = tab.scope === 'PLATFORM';
                    return (
                        <Gate
                            key={tab.to}
                            permission={tab.permission}
                            scope={isPlatformScoped ? 'PLATFORM' : 'TENANT'}
                            scopeRef={isPlatformScoped ? undefined : tenantId}
                        >
                            {isActive ? (
                                <span
                                    aria-current="page"
                                    className="border-b-2 border-brand-accent px-3 py-2 text-sm font-semibold"
                                >
                                    {tab.label}
                                </span>
                            ) : (
                                <Link
                                    to={to}
                                    className="border-b-2 border-transparent px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                                >
                                    {tab.label}
                                </Link>
                            )}
                        </Gate>
                    );
                })}
            </nav>
        </div>
    );
}

type TabDef = { to: string; label: string; permission: Permission; scope?: Scope };

// Pestañas comunes a toda organización, antes de las de recurso.
const COMMON_HEAD_TABS: TabDef[] = [
    { to: 'users', label: 'Usuarios', permission: 'tenant.user.read' },
    { to: 'roles', label: 'Roles', permission: 'tenant.role.list' },
];

// Pestañas comunes a toda organización, después de las de recurso; "Campos" es la única con
// alcance de plataforma, no de tenant.
const COMMON_TAIL_TABS: TabDef[] = [
    { to: 'branding', label: 'Marca', permission: 'tenant.branding.manage' },
    // Las integraciones solo las administra plataforma; el gate va a TENANT/{tenantId}, igual que el backend.
    { to: 'integrations', label: 'Integraciones', permission: 'platform.integration.view' },
    {
        to: 'user-fields',
        label: 'Campos',
        permission: 'platform.tenant.userfields.manage',
        scope: 'PLATFORM',
    },
];

// Permiso de gestión de los recursos de un nivel GENÉRICO: `tenant.level.manage` (SYSTEM, scope TENANT).
// Para los niveles nuevos (LEVEL_1/2/3) NO hay un permiso por recurso — toda la gestión de entidades de
// nivel va por esta key. El platform admin (scope PLATFORM '*') la cubre por rank; el backend re-enforza.
// Si en algún caso el permiso no alcanzara, el server-side igual gatea la pantalla.
const LEVEL_MANAGE_PERMISSION: Permission = 'tenant.level.manage';

/**
 * Tabs de RECURSO derivados de los niveles de la app del tenant (`tenant.levels`). Un tab por nivel,
 * con `label` = `level.displayName` (el nombre de negocio — NUNCA el `level` crudo). Todos los niveles
 * van a la pantalla GENÉRICA `/platform/tenants/{id}/levels/{level}`, gate `tenant.level.manage` a scope
 * TENANT del path.
 *
 * PURGA FASE 4 (DEC-RBAC-091): las pantallas BESPOKE de niveles semánticos legacy (retailers/stores/
 * groups) fueron eliminadas junto con el mapa `LEGACY_LEVEL_ROUTE`. Un tenant legacy no migrado (con
 * niveles RETAILER/STORE/GROUP) cae a la rama genérica; la pantalla `/levels/{level}` valida `isLevelName`
 * (solo LEVEL_*) → esos niveles semánticos ya no tienen pantalla de recurso hasta migrarse a LEVEL_N.
 *
 * Los niveles llegan en el orden que define la app (jerárquico), que es el orden natural de navegación del
 * recurso, así que se respeta (no se reordena alfabéticamente). `levels` ausente o vacío → sin tabs de recurso.
 */
function tabsForLevels(levels: TenantLevelLocal[] | undefined): TabDef[] {
    const resourceTabs: TabDef[] = (levels ?? []).map((lvl) => ({
        to: `levels/${lvl.level}`,
        label: lvl.displayName,
        permission: LEVEL_MANAGE_PERMISSION,
    }));
    return [...COMMON_HEAD_TABS, ...resourceTabs, ...COMMON_TAIL_TABS];
}

// Exportado para testear la RESOLUCIÓN de niveles con prioridad (tenant.levels → app.levels) sin el
// router file-based: el test mockea Route.useParams/useRouterState, platformApi.getTenant y useApplication.
export function TenantShell() {
    const { tenantId } = Route.useParams();
    const pathname = useRouterState({ select: (s) => s.location.pathname });

    const {
        data: tenant,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'detail'],
        queryFn: () => platformApi.getTenant(tenantId),
    });

    // Niveles de RECURSO con prioridad (mismo patrón que EditTenantModal / T8: derivar de la app):
    //  1) `tenant.levels` si el backend ya enriqueció el detalle → fuente directa.
    //  2) si llega vacío/ausente y el tenant tiene `applicationId` → se resuelven de la APP.
    // Mientras la app carga no se renderizan tabs de nivel a medias: `resolvedLevels` queda undefined
    // hasta que la app resuelva. Lógica compartida con `useTenantLabelResolver` — ver `useResolvedTenantLevels`.
    const resolvedLevels = useResolvedTenantLevels(tenant);

    return (
        <>
            <TenantSubNav
                tenantId={tenantId}
                displayName={tenant?.displayName}
                isError={isError}
                pathname={pathname}
                levels={resolvedLevels}
            />
            {isError && (
                <div className="mt-4">
                    <ErrorState
                        title="No pudimos cargar la organización"
                        message={resolveErrorMessage(error)}
                        retryLabel="Reintentar"
                        onRetry={() => {
                            void refetch();
                        }}
                    />
                </div>
            )}
            <div className="mt-4">
                <Outlet />
            </div>
        </>
    );
}
